'use client';

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useRouter } from 'next/navigation';
import { emitirPerfilActualizado } from '@/lib/perfil-actualizado';
import { BellRing, Check, FileText, FolderKanban, Megaphone, Phone, SlidersHorizontal } from 'lucide-react';
import { ModalShell } from '@/components/ui/ModalShell';
import { VisibilityToggle } from '@/components/ui/VisibilityToggle';
import { notify } from '@/components/ui/BrandToaster';
import { ConsentimientoVoceriaModal } from '@/components/consentimientos/ConsentimientoVoceriaModal';
import {
	CONSENTIMIENTO_VOCERIA,
	PREFERENCIAS_PROXIMAMENTE,
	getMisPreferencias,
	getPreferenciasDeInvestigador,
	responderConsentimientoVoceria,
	type EstadoConsentimiento,
} from '@/lib/preferencias';
import {
	TELEFONO_CODIGO,
	agregarDatoPersonal,
	formatearTelefonoChile,
	getDatosPersonales,
	getIdTipoDato,
	telefonoDe,
} from '@/lib/datos-personales';
import formStyles from '@/components/ui/form.module.css';
import modalStyles from '@/components/ui/modal.module.css';
import styles from './preferencias.module.css';

/**
 * Hub de Preferencias (R62). UN modal que se transforma, no modales apilados:
 * mismo patrón que `IdentityHubModal` —`DIMS` por vista, la tarjeta anima su
 * tamaño, cada vista se dibuja con `ModalShell embedded`— porque encadenar
 * "preferencias → consentimiento → teléfono" con overlays superpuestos deja tres
 * capas grises y ningún camino claro de vuelta.
 *
 * Las tres vistas:
 *   · `lista`    — las preferencias de la cuenta.
 *   · `voceria`  — el texto completo del consentimiento (la MISMA pieza que se
 *                  muestra sobre "Editar información personal", acá embebida).
 *   · `telefono` — alta del número, para no mandar a nadie a otra pantalla.
 *
 * `soloLectura` es la MISMA pieza vista por un administrador desde el perfil
 * ajeno (R63): las mismas filas y el mismo texto de consentimiento, sin ninguna
 * de las salidas que escriben. Se eligió reusarla en vez de escribir una vista
 * "de admin" porque lo que el administrador necesita ver es exactamente lo que
 * la persona ve — si un día divergen, deja de servir para responder "¿qué le
 * aparece a ella?".
 */

type Vista = 'lista' | 'voceria' | 'telefono';

const DIMS: Record<Vista, { w: string; h: string }> = {
	lista: { w: '62rem', h: '38rem' },
	// EXACTAMENTE la tarjeta del diálogo suelto (`consentimiento.module.css`:
	// max-width 928px, min-height 384px). Tiene que ser el mismo tamaño porque es
	// el mismo texto y el mismo diálogo; si allá cambia, acá también. Y se eligió
	// cerca de la lista (62rem) para que pasar de una vista a otra no se sienta
	// como abrir otro modal.
	voceria: { w: '58rem', h: '24rem' },
	// 52rem no es un número redondo por gusto: con ~784px de caja el párrafo de
	// entrada (unos 205 caracteres) entra en DOS líneas. A 44rem caía a tres y el
	// justificado abría huecos entre palabras para rellenar la última. Menos alto
	// también, porque una línea menos deja de necesitarlo.
	telefono: { w: '52rem', h: '28rem' },
};

/**
 * El icono de cada preferencia futura. La LISTA vive en `lib/preferencias.ts`
 * porque la comparte la gestión de usuarios; acá queda sólo lo que es dibujo.
 */
const ICONO_PROXIMAMENTE: Record<string, typeof FileText> = {
	'notif-new-publication': FileText,
	'notif-project-status': FolderKanban,
	'notif-new-call': BellRing,
};

/** Qué dice y de qué color va el estado en la fila. `null` se lee igual que
    `sin_responder`: desde la fila, ambos son "todavía no hay respuesta". */
const ETIQUETA_ESTADO: Record<EstadoConsentimiento, string> = {
	aceptado: 'Autorizado',
	rechazado: 'No autorizado',
	sin_responder: 'Sin responder',
};

const TONO_ESTADO: Record<EstadoConsentimiento, string> = {
	aceptado: styles.prefBadgeSi,
	rechazado: styles.prefBadgeNo,
	sin_responder: '',
};

// Abreviada: la fecha acompaña al estado en la misma línea, y "9 de septiembre
// de 2026" ahí dentro pesa más que el estado al que acompaña.
const FECHA_CORTA = new Intl.DateTimeFormat('es-CL', {
	day: 'numeric',
	month: 'short',
	year: 'numeric',
});

interface PreferenciasHubModalProps {
	/** Perfil cuyas preferencias se muestran: de ahí salen sus datos personales. */
	slug: string;
	/**
	 * Vista de administrador sobre el perfil de OTRA persona: se lee todo y no se
	 * escribe nada. El consentimiento sólo se reinicia, y eso vive en la gestión
	 * de usuarios (`/admin/configuraciones`), no acá.
	 */
	soloLectura?: boolean;
	/** Nombre visible del dueño del perfil: sólo se usa para titular la vista ajena. */
	nombre?: string | null;
	onClose: () => void;
}

export function PreferenciasHubModal({
	slug,
	soloLectura = false,
	nombre,
	onClose,
}: PreferenciasHubModalProps) {
	const [vista, setVista] = useState<Vista>('lista');
	const [estado, setEstado] = useState<EstadoConsentimiento | null>(null);
	const [respondidoAt, setRespondidoAt] = useState<string | null>(null);
	const [telefono, setTelefono] = useState<string | null>(null);
	const [carga, setCarga] = useState<'cargando' | 'listo' | 'error'>('cargando');
	const [guardando, setGuardando] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [telDraft, setTelDraft] = useState('');
	const [telPublico, setTelPublico] = useState(false);
	const [telError, setTelError] = useState<string | null>(null);
	const [reciénAutorizado, setReciénAutorizado] = useState(false);
	const router = useRouter();

	const volver = useCallback(() => {
		setError(null);
		setTelError(null);
		setVista('lista');
	}, []);

	// Esc: primero deshace la vista, y sólo desde la lista cierra. Así no se pierde
	// de un tecleo el camino andado.
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== 'Escape' || guardando) return;
			if (vista === 'lista') onClose();
			else volver();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [vista, guardando, onClose, volver]);

	useEffect(() => {
		let cancelado = false;
		Promise.all([
			soloLectura ? getPreferenciasDeInvestigador(slug) : getMisPreferencias(),
			// Si los datos personales fallan (por ejemplo, un 403), el hub sigue
			// sirviendo: sólo pierde la validación del teléfono.
			getDatosPersonales(slug).catch(() => null),
		])
			.then(([prefs, datos]) => {
				if (cancelado) return;
				setEstado(prefs.voceriaConsentimiento);
				setRespondidoAt(prefs.voceriaConsentimientoAt);
				setTelefono(datos ? telefonoDe(datos) : null);
				setCarga('listo');
			})
			.catch(() => {
				if (!cancelado) setCarga('error');
			});
		return () => {
			cancelado = true;
		};
	}, [slug, soloLectura]);

	async function responder(siguiente: EstadoConsentimiento) {
		setGuardando(true);
		setError(null);
		try {
			const prefs = await responderConsentimientoVoceria(siguiente);
			setEstado(prefs.voceriaConsentimiento);
			setRespondidoAt(prefs.voceriaConsentimientoAt);
			if (siguiente === 'aceptado') {
				notify.success('Autorización registrada', {
					description: 'Puedes cambiarla cuando quieras en Preferencias.',
				});
				// Autorizar sin dejar un número no sirve de nada: el hub encadena solo.
				setReciénAutorizado(!telefono);
				setVista(telefono ? 'lista' : 'telefono');
				return;
			}
			notify.success('Respuesta registrada', {
				description: 'No te contactarán para prensa. Puedes cambiarlo en Preferencias.',
			});
			setVista('lista');
		} catch {
			setError('No se pudo guardar tu respuesta. Intenta de nuevo.');
		} finally {
			setGuardando(false);
		}
	}

	// El id del tipo se resuelve una vez y se recuerda: el catálogo no cambia
	// mientras el modal está abierto.
	const idTipoTelefono = useRef<number | null>(null);

	async function guardarTelefono(event: React.FormEvent) {
		event.preventDefault();
		const valor = formatearTelefonoChile(telDraft.trim());
		// El input `type="tel"` NO valida nada por sí solo (a propósito: los formatos
		// telefónicos del mundo no entran en un patrón), así que la regla la ponemos
		// acá: dígitos y los separadores de siempre, y al menos 8 dígitos reales.
		if (!/^[\d+()\s.-]+$/.test(valor)) {
			setTelError('Escribe solo números. Puedes usar +, espacios, guiones o paréntesis.');
			return;
		}
		if ((valor.match(/\d/g) ?? []).length < 8) {
			setTelError('Faltan dígitos: escribe el número completo.');
			return;
		}
		setGuardando(true);
		setTelError(null);
		try {
			idTipoTelefono.current ??= await getIdTipoDato(TELEFONO_CODIGO);
			if (idTipoTelefono.current === null) {
				setTelError('El teléfono no está disponible como dato en este momento.');
				return;
			}
			const datos = await agregarDatoPersonal(slug, {
				idTipoDatoPersonal: idTipoTelefono.current,
				valorTexto: valor,
				esPublico: telPublico,
			});
			setTelefono(telefonoDe(datos));
			setTelDraft('');
			// Tres cosas distintas, y hacen falta las tres (ver AGENTS.md):
			//   · la revalidación (dentro de `agregarDatoPersonal`) invalida el caché
			//     del SERVIDOR para la próxima visita;
			//   · `router.refresh()` vuelve a traer el árbol de servidor de esta ruta;
			//   · el aviso despierta a `OwnerCvView`, que NO se pinta en el servidor
			//     —trae sus datos con un fetch propio— y por eso no se entera de
			//     ninguna de las dos anteriores.
			router.refresh();
			emitirPerfilActualizado(slug);
			notify.success('Teléfono agregado', {
				description: telPublico
					? 'Queda visible en tu perfil público.'
					: 'Queda oculto en tu perfil público.',
			});
			setVista('lista');
		} catch {
			setTelError('No se pudo guardar el teléfono. Intenta de nuevo.');
		} finally {
			setGuardando(false);
		}
	}

	const dims = DIMS[vista];
	const bloqueado = carga !== 'listo' || guardando;
	const faltaTelefono = carga === 'listo' && estado === 'aceptado' && !telefono;
	// "Sin responder" no es una respuesta que mostrar: desde la fila, cerrar el
	// diálogo sin elegir y no haber abierto nunca la consulta son lo mismo.
	const hayRespuesta = carga === 'listo' && (estado === 'aceptado' || estado === 'rechazado');
	// …salvo mirando el perfil ajeno, donde es al revés: el administrador entra
	// justamente a saber en qué quedó, y una fila sin chip se lee como "no hay
	// dato" cuando el dato es "todavía no responde".
	const mostrarEstado = soloLectura ? carga === 'listo' : hayRespuesta;
	const etiquetaEstado = estado ? ETIQUETA_ESTADO[estado] : 'Nunca se le preguntó';
	const tonoEstado = estado ? TONO_ESTADO[estado] : '';

	let contenido: React.ReactNode;

	if (vista === 'voceria') {
		contenido = (
			<ConsentimientoVoceriaModal
				embedded
				paso="pregunta"
				soloLectura={soloLectura}
				onVolver={volver}
				onCerrar={onClose}
				onAceptar={() => void responder('aceptado')}
				onRechazar={() => void responder('rechazado')}
				onListo={volver}
				busy={guardando}
				error={error}
			/>
		);
	} else if (vista === 'telefono') {
		contenido = (
			<ModalShell
				embedded
				title="Agregar teléfono"
				icon={<Phone size={16} />}
				onClose={onClose}
				onBack={volver}
				onCancel={volver}
				cancelText={reciénAutorizado ? 'Ahora no' : 'Cancelar'}
				formId="form-telefono-preferencias"
				saveText={guardando ? 'Guardando…' : 'Guardar'}
				saveDisabled={guardando}
				hideFooterIndicator
			>
				<form id="form-telefono-preferencias" className={styles.telForm} onSubmit={guardarTelefono}>
					{reciénAutorizado ? <p className={styles.telTitulo}>¡Muchas gracias!</p> : null}
					<p className={styles.telIntro}>
						Si quieres, puedes dejar tu teléfono acá mismo: es el número con el que el
						Laboratorio podría ubicarte. No es obligatorio, y lo puedes agregar, cambiar o
						borrar cuando quieras desde tu información personal.
					</p>

					<div className={styles.telCampo}>
						<label className={styles.telLabel} htmlFor="tel-preferencias">
							Teléfono
						</label>
						<div className={styles.telFila}>
							{/* El input compartido de la app (36px, mismo borde y foco que todos
							    los campos), no una copia local. */}
							<input
								id="tel-preferencias"
								className={formStyles.modalInput}
								style={telError ? { borderColor: '#fca5a5' } : undefined}
								type="tel"
								inputMode="tel"
								autoComplete="tel"
								maxLength={32}
								autoFocus
								value={telDraft}
								placeholder="+56 9 1234 5678"
								onChange={(e) => {
									// Se formatea al vuelo para que lo que se ve sea lo que se guarda.
									setTelDraft(formatearTelefonoChile(e.target.value));
									setTelError(null);
								}}
							/>
							<VisibilityToggle
								isPublic={telPublico}
								onToggle={() => setTelPublico((v) => !v)}
								disabled={guardando}
								ariaLabel={
									telPublico ? 'Ocultar el teléfono del perfil' : 'Mostrar el teléfono en el perfil'
								}
							/>
						</div>
						{telError ? <span className={styles.telError}>{telError}</span> : null}
					</div>

					<p className={styles.telNota}>
						<strong>Oculto</strong> significa que no aparece en tu perfil público; igual pueden
						contactarte desde la universidad.
					</p>
				</form>
			</ModalShell>
		);
	} else {
		contenido = (
			<ModalShell
				embedded
				title={soloLectura ? `Preferencias de ${nombre ?? 'la persona'}` : 'Preferencias'}
				icon={<SlidersHorizontal size={16} />}
				onClose={onClose}
				onCancel={onClose}
				cancelText="Cerrar"
				cancelTone="brand"
				showSaveAction={false}
				hideFooterIndicator
			>
				<ul className={styles.prefList}>
					<li className={styles.prefRow}>
						<span className={styles.prefIcon}>
							<Megaphone size={17} aria-hidden="true" />
						</span>

						<div className={styles.prefMain}>
							<p className={styles.prefTitle}>{CONSENTIMIENTO_VOCERIA.label}</p>
							<p className={styles.prefDesc}>{CONSENTIMIENTO_VOCERIA.descripcion}</p>

							{carga === 'error' ? (
								<p className={styles.prefError}>
									{soloLectura
										? 'No pudimos cargar estas preferencias. Cierra y vuelve a abrir para reintentar.'
										: 'No pudimos cargar tus preferencias. Cierra y vuelve a abrir para reintentar.'}
								</p>
							) : null}
							{error ? <p className={styles.prefError}>{error}</p> : null}

							{faltaTelefono ? (
								<p className={styles.prefAviso}>
									<span>
										{soloLectura
											? 'Autorizó, pero todavía no registra un teléfono de contacto.'
											: 'Todavía no registras un teléfono de contacto.'}
									</span>
									{/* El alta del número es del dueño. Un administrador que necesite cargarlo
									    lo hace en el editor de perfil, donde queda auditado como lo que es:
									    una edición de datos personales. */}
									{soloLectura ? null : (
										<button
											type="button"
											className={styles.prefAvisoBtn}
											onClick={() => {
												setReciénAutorizado(false);
												setVista('telefono');
											}}
										>
											Agregar
										</button>
									)}
								</p>
							) : null}

							{carga === 'listo' && estado === 'aceptado' && telefono ? (
								<p className={styles.prefDato}>
									<Check size={13} aria-hidden="true" />
									{soloLectura
									? `Lo pueden contactar al ${telefono}`
									: `Te pueden contactar al ${telefono}`}
								</p>
							) : null}
						</div>

						<div className={styles.prefControl}>
							{/* Sin respuesta no hay estado que mostrar: el chip diría lo mismo
							    que el botón. Con respuesta, el chip informa y el botón pasa a
							    ser secundario, para revisar o cambiar. */}
							{mostrarEstado ? (
								<span className={styles.prefEstadoLinea}>
									{respondidoAt ? (
										<span className={styles.prefMeta}>
											{FECHA_CORTA.format(new Date(respondidoAt))}
										</span>
									) : null}
									<span className={`${styles.prefBadge} ${tonoEstado}`}>{etiquetaEstado}</span>
								</span>
							) : null}

							{/* El botón abre el TEXTO. Responder desde la fila con un
							    "Autorizo / No autorizo" pedía consentir sin haber mostrado a
							    qué, que es justo lo que un consentimiento no puede hacer. */}
							<button
								type="button"
								className={`${styles.prefVerBtn} ${
									hayRespuesta || soloLectura ? '' : styles.prefVerBtnPrimario
								}`}
								onClick={() => setVista('voceria')}
								disabled={bloqueado}
							>
								<FileText size={13} aria-hidden="true" />
								{hayRespuesta || soloLectura ? 'Ver consentimiento' : 'Responder ahora'}
							</button>
						</div>
					</li>

					{PREFERENCIAS_PROXIMAMENTE.map(({ id, titulo, descripcion }) => {
						const Icono = ICONO_PROXIMAMENTE[id] ?? BellRing;
						return (
							<li key={id} className={`${styles.prefRow} ${styles.prefRowMuted}`}>
								<span className={styles.prefIcon}>
									<Icono size={17} aria-hidden="true" />
								</span>
								<div className={styles.prefMain}>
									<p className={styles.prefTitle}>{titulo}</p>
									<p className={styles.prefDesc}>{descripcion}</p>
								</div>
								<div className={styles.prefControl}>
									<span className={styles.prefBadge}>Próximamente</span>
								</div>
							</li>
						);
					})}
				</ul>
			</ModalShell>
		);
	}

	return (
		<div className={modalStyles.modalOverlay}>
			<div
				className={`${modalStyles.modalCard} ${styles.hubCard}`}
				onClick={(e) => e.stopPropagation()}
				// El alto viaja como VARIABLE, no como estilo en línea: en el teléfono
				// `.hubCard` lo anula con `height: auto` (una tarjeta de 38rem clavada
				// en una pantalla de 360px deja media vista vacía), y un estilo en
				// línea le gana a cualquier media query. El ancho sí queda en línea:
				// es el que tiene que vencer al `max-width` de `.modalCard`.
				style={{ maxWidth: dims.w, '--hub-h': dims.h } as CSSProperties}
			>
				{/* `key` fuerza el remonte al cambiar de vista: es lo que dispara el
				    fundido de entrada y, de paso, evita que React reconcilie el
				    `ModalShell` de una vista con la pieza de otra. */}
				<div key={vista} className={styles.hubView}>
					{contenido}
				</div>
			</div>
		</div>
	);
}
